import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { X, Check, Award, Bell, AlertCircle } from 'lucide-react';

const slideIn = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const slideOut = keyframes`
  from { transform: translateX(0); opacity: 1; }
  to { transform: translateX(100%); opacity: 0; }
`;

const ToastContainer = styled.div`
  position: fixed;
  top: 80px;
  right: 340px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none;

  @media (max-width: 1200px) {
    right: 20px;
  }

  @media (max-width: 768px) {
    right: 10px;
    left: 10px;
    top: calc(var(--polka-safe-top, 0px) + 70px);
  }
`;

const ToastItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: ${({ theme }) => theme.isDarkMode ? '#18191B' : '#FFFFFF'};
  border: 1px solid ${({ theme }) => theme.isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};
  border-radius: 14px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
  pointer-events: auto;
  animation: ${({ $exiting }) => $exiting ? slideOut : slideIn} 0.3s ease forwards;
  max-width: 400px;

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 12px 14px;
  }
`;

const ToastIcon = styled.div`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $type }) => 
    $type === 'achievement' ? '#F59E0B20' : 
    $type === 'error' ? '#EF444420' : 
    '#10B98120'};
  color: ${({ $type }) => 
    $type === 'achievement' ? '#F59E0B' : 
    $type === 'error' ? '#EF4444' : 
    '#10B981'};
`;

const ToastContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ToastTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 2px;
`;

const ToastMessage = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ToastClose = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
  transition: opacity 0.2s;
  &:hover { opacity: 1; }
`;

// Глобальное хранилище тостов
let toastListeners = [];
let toastIdCounter = 0;

// eslint-disable-next-line react-refresh/only-export-components
export const showToast = ({ title, message, type = 'info', duration = 4000, action }) => {
  const id = ++toastIdCounter;
  const toast = { id, title, message, type, duration, action, exiting: false };
  toastListeners.forEach(fn => fn(toast));
  return id;
};

// eslint-disable-next-line react-refresh/only-export-components
export const dismissToast = (id) => {
  toastListeners.forEach(fn => fn({ id, dismiss: true }));
};

const GlobalToast = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    if (toast.dismiss) {
      setToasts(prev => prev.map(t => t.id === toast.id ? { ...t, exiting: true } : t));
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== toast.id));
      }, 300);
      return;
    }
    setToasts(prev => [...prev, toast]);
    setTimeout(() => {
      setToasts(prev => prev.map(t => t.id === toast.id ? { ...t, exiting: true } : t));
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== toast.id));
      }, 300);
    }, toast.duration || 4000);
  }, []);

  useEffect(() => {
    toastListeners.push(addToast);
    return () => {
      toastListeners = toastListeners.filter(fn => fn !== addToast);
    };
  }, [addToast]);

  const handleDismiss = (id) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 300);
  };

  const getIcon = (type) => {
    switch (type) {
      case 'achievement': return <Award size={18} />;
      case 'error': return <AlertCircle size={18} />;
      case 'success': return <Check size={18} />;
      default: return <Bell size={18} />;
    }
  };

  if (toasts.length === 0) return null;

  return (
    <ToastContainer>
      {toasts.map(toast => (
        <ToastItem key={toast.id} $exiting={toast.exiting} role={toast.type === 'error' ? 'alert' : 'status'}>
          <ToastIcon $type={toast.type}>
            {getIcon(toast.type)}
          </ToastIcon>
          <ToastContent>
            {toast.title && <ToastTitle>{toast.title}</ToastTitle>}
            {toast.message && <ToastMessage>{toast.message}</ToastMessage>}
          </ToastContent>
          {toast.action && <button type="button" style={{ background: 'none', border: 0, color: 'inherit', fontWeight: 600, padding: 8, cursor: 'pointer' }} onClick={() => { toast.action.onClick(); handleDismiss(toast.id); }}>{toast.action.label}</button>}
          <ToastClose aria-label="Закрыть уведомление" onClick={() => handleDismiss(toast.id)}>
            <X size={14} />
          </ToastClose>
        </ToastItem>
      ))}
    </ToastContainer>
  );
};

export default GlobalToast;
